/**
 * Inline shipping address form for spin-wheel product claims (stay in popup flow).
 * Same payloads as ShippingAddressScreen / api/user/shipping-addresses.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Modal,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Alert,
  Pressable,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Constants from 'expo-constants';
import { APIProvider } from '@vis.gl/react-google-maps';
import {
  createShippingAddress,
  updateShippingAddress,
  getShippingAddresses,
  getShippingAddress,
} from '../services/shippingAddressApi';
import MapComponent from './maps/MapComponent';
import LocationAutoComplete from './maps/LocationAutoComplete';
import AddressWilayahPicker from './AddressWilayahPicker';
import { getDeviceCoordinates, isValidMapCoordinate } from '../utils/deviceGeolocation';
import { fetchGeomap, parseGeocodeLabel, resolveBiteshipArea, formatWilayahLabel } from '../utils/shippingPinpoint';

const MAX_DETAIL = 255;

export default function SpinWheelShippingAddressModal({
  visible,
  onDismiss,
  onAddressSaved,
  /** When true, backdrop / Batal cannot close the modal (spin claim must complete via form submit). */
  blockDismiss = false,
  /** Load & PATCH this row — kelola alamat utama di popup (tanpa pindah screen). */
  editId = null,
  title,
  subtitle,
  saveLabel,
}) {
  const [bootLoading, setBootLoading] = useState(false);
  const [namaAlamat, setNamaAlamat] = useState('');
  const [namaPenerima, setNamaPenerima] = useState('');
  const [noTelp, setNoTelp] = useState('');
  const [provinsi, setProvinsi] = useState('');
  const [kota, setKota] = useState('');
  const [kecamatan, setKecamatan] = useState('');
  const [kodepos, setKodepos] = useState('');
  const [detailAlamat, setDetailAlamat] = useState('');
  const [catatan, setCatatan] = useState('');
  const [kodeBiteship, setKodeBiteship] = useState('');
  const [latitude, setLatitude] = useState('0');
  const [longitude, setLongitude] = useState('0');
  const [addressCoordinate, setAddressCoordinate] = useState(null);
  const [currentAddress, setCurrentAddress] = useState(null);
  const [locating, setLocating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const submitLock = useRef(false);
  const pinSeq = useRef(0);
  const wilayahLockedRef = useRef(false);
  const [priorAddressCount, setPriorAddressCount] = useState(null);
  const [wilayahPickerOpen, setWilayahPickerOpen] = useState(false);
  const isEditFlow = editId != null && Number(editId) > 0;

  const applyCoordinate = useCallback((coord, options = {}) => {
    if (!coord) return;
    const fillWilayah = options.fillWilayah !== false;
    const seq = ++pinSeq.current;
    setAddressCoordinate(coord);
    setLatitude(String(coord.latitude ?? '0'));
    setLongitude(String(coord.longitude ?? '0'));
    void (async () => {
      try {
        const data = await fetchGeomap(coord.latitude, coord.longitude);
        if (seq !== pinSeq.current) return;
        setCurrentAddress(parseGeocodeLabel(data));
        if (!fillWilayah || wilayahLockedRef.current) return;
        const area = await resolveBiteshipArea(data);
        if (seq !== pinSeq.current || wilayahLockedRef.current) return;
        if (area) {
          setProvinsi(area.nama_provinsi || '');
          setKota(area.nama_kota || '');
          setKecamatan(area.nama_kecamatan || '');
          setKodepos(String(area.kodepos || ''));
          setKodeBiteship(area.kode || '');
        }
      } catch {
        if (seq !== pinSeq.current) return;
        setCurrentAddress('Location not found');
      }
    })();
  }, []);

  const onPinChange = useCallback((coord) => {
    wilayahLockedRef.current = false;
    applyCoordinate(coord, { fillWilayah: true });
  }, [applyCoordinate]);

  const getCurrentLocation = useCallback(async () => {
    if (locating) return;
    setLocating(true);
    try {
      const coord = await getDeviceCoordinates();
      onPinChange(coord);
    } catch (e) {
      Alert.alert('Perhatian', e.message || 'Tidak bisa mengambil lokasi saat ini.');
    } finally {
      setLocating(false);
    }
  }, [onPinChange, locating]);

  useEffect(() => {
    if (!visible) {
      pinSeq.current += 1;
      return;
    }
    setSaving(false);
    setFormError('');
    setWilayahPickerOpen(false);
    pinSeq.current += 1;
    wilayahLockedRef.current = false;
    let cancelled = false;

    const eid = editId != null && Number(editId) > 0 ? Number(editId) : 0;
    if (eid > 0) {
      setPriorAddressCount(null);
      setBootLoading(true);
      setNamaAlamat('');
      setNamaPenerima('');
      setNoTelp('');
      setProvinsi('');
      setKota('');
      setKecamatan('');
      setKodepos('');
      setDetailAlamat('');
      setCatatan('');
      setKodeBiteship('');
      setAddressCoordinate(null);
      setCurrentAddress(null);
      (async () => {
        try {
          const a = await getShippingAddress(eid);
          if (cancelled || !a) return;
          setNamaAlamat(String(a.nama_alamat || '').trim());
          setNamaPenerima(String(a.nama_penerima || '').trim());
          setNoTelp(String(a.no_telp || '').trim());
          setProvinsi(String(a.provinsi || '').trim());
          setKota(String(a.kota || '').trim());
          setKecamatan(String(a.kecamatan || '').trim());
          setKodepos(String(a.kodepos || '').trim());
          setDetailAlamat(String(a.detail_alamat || '').trim());
          setCatatan(String(a.catatan || '').trim());
          setKodeBiteship(String(a.kode_biteship || '').trim());
          const hasWilayah = !!(
            String(a.kode_biteship || '').trim()
            || (String(a.provinsi || '').trim()
              && String(a.kota || '').trim()
              && String(a.kecamatan || '').trim()
              && String(a.kodepos || '').trim())
          );
          wilayahLockedRef.current = hasWilayah;
          const lat = Number(a.latitude);
          const lng = Number(a.longitude);
          if (isValidMapCoordinate(lat, lng)) {
            applyCoordinate({ latitude: lat, longitude: lng }, { fillWilayah: !hasWilayah });
          } else {
            setAddressCoordinate(null);
            setLatitude('0');
            setLongitude('0');
            setCurrentAddress(null);
          }
        } catch {
          if (!cancelled) Alert.alert('Gagal memuat', 'Tidak dapat memuat data alamat.');
        } finally {
          if (!cancelled) setBootLoading(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    setBootLoading(false);
    setNamaAlamat('');
    setNamaPenerima('');
    setNoTelp('');
    setProvinsi('');
    setKota('');
    setKecamatan('');
    setKodepos('');
    setDetailAlamat('');
    setCatatan('');
    setKodeBiteship('');
    setLatitude('0');
    setLongitude('0');
    setAddressCoordinate(null);
    setCurrentAddress(null);
    setPriorAddressCount(null);
    (async () => {
      try {
        const rows = await getShippingAddresses();
        if (!cancelled) setPriorAddressCount(rows.length);
      } catch {
        if (!cancelled) setPriorAddressCount(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, editId, applyCoordinate]);

  const showFormError = (msg) => {
    setFormError(msg);
    Alert.alert('Perhatian', msg);
  };

  const addrIdOf = (addr, fallback = 0) => {
    const n = Number(addr?.id ?? fallback ?? 0);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };

  const onSubmit = useCallback(async () => {
    if (saving || bootLoading || submitLock.current) return;
    setFormError('');
    const d = detailAlamat.trim();
    const wilayahLengkap = !!(provinsi.trim() && kota.trim() && kecamatan.trim() && kodepos.trim());
    if (!namaAlamat.trim() || !namaPenerima.trim() || !d) {
      showFormError('Isi nama alamat, nama penerima, dan detail alamat.');
      return;
    }
    if (d.length > MAX_DETAIL) {
      showFormError(`Detail alamat maksimal ${MAX_DETAIL} karakter.`);
      return;
    }
    if (!noTelp.trim()) {
      showFormError('Nomor telepon wajib diisi.');
      return;
    }
    if (!addressCoordinate) {
      showFormError('Belum pinpoint lokasi alamatmu.');
      return;
    }
    if (!wilayahLengkap) {
      showFormError('Pilih provinsi, kota/kab, dan kode pos.');
      return;
    }

    submitLock.current = true;
    setSaving(true);
    try {
      const payload = {
        nama_alamat: namaAlamat.trim(),
        nama_penerima: namaPenerima.trim(),
        no_telp: noTelp.trim(),
        detail_alamat: d,
        provinsi: provinsi.trim(),
        kota: kota.trim(),
        kecamatan: kecamatan.trim(),
        kodepos: kodepos.trim(),
        catatan: catatan.trim(),
        kode_biteship: kodeBiteship.trim(),
        latitude: String(addressCoordinate?.latitude ?? latitude ?? '0'),
        longitude: String(addressCoordinate?.longitude ?? longitude ?? '0'),
      };
      if (!isEditFlow) {
        payload.set_as_primary = true;
      }

      if (isEditFlow) {
        const eid = Number(editId);
        const addr = await updateShippingAddress(eid, payload);
        const aid = addrIdOf(addr, eid);
        if (aid < 1) {
          showFormError('Alamat tidak memiliki ID. Coba lagi.');
          return;
        }
        onAddressSaved({ ...(addr || {}), id: aid });
        return;
      }

      const addr = await createShippingAddress(payload);
      const aid = addrIdOf(addr);
      if (aid < 1) {
        showFormError('Alamat tidak memiliki ID. Coba lagi.');
        return;
      }
      onAddressSaved({ ...(addr || {}), id: aid });
    } catch (e) {
      showFormError(e.message || 'Gagal menyimpan alamat. Coba lagi.');
    } finally {
      submitLock.current = false;
      setSaving(false);
    }
  }, [
    saving,
    bootLoading,
    namaAlamat,
    namaPenerima,
    noTelp,
    detailAlamat,
    provinsi,
    kota,
    kecamatan,
    kodepos,
    kodeBiteship,
    catatan,
    latitude,
    longitude,
    addressCoordinate,
    onAddressSaved,
    editId,
    isEditFlow,
  ]);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={blockDismiss ? () => {} : onDismiss}
    >
      <KeyboardAvoidingView
        style={styles.overlay}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        pointerEvents="box-none"
      >
        <Pressable
          style={styles.backdrop}
          onPress={onDismiss}
          disabled={blockDismiss || bootLoading || saving}
        />
        <View style={styles.card} pointerEvents="auto">
          <Text style={styles.title}>
            {title
              || (isEditFlow ? 'Ubah alamat utama' : 'Alamat pengiriman hadiah')}
          </Text>
          <Text style={styles.sub}>
            {subtitle
              || (isEditFlow
                ? 'Perbarui tanpa meninggalkan alur klaim hadiah spin. Sesudah simpan kamu bisa cek lagi lalu kirim klaim.'
                : priorAddressCount === null
                  ? 'Isi data berikut untuk melanjutkan klaim.'
                  : priorAddressCount === 0
                    ? 'Kamu belum punya alamat tersimpan. Alamat ini otomatis diset sebagai alamat utama.'
                    : 'Alamat baru ini dijadikan alamat utama agar pengiriman hadiah spin mengikutinya — atur atau hapus alamat lain di Profil → Alamat pengiriman.')}
          </Text>
          <ScrollView style={styles.scroll} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
            {bootLoading ? (
              <View style={styles.bootWrap}>
                <ActivityIndicator size="large" color="#64748B" />
                <Text style={styles.bootText}>Memuat data alamat…</Text>
              </View>
            ) : (
              <>
            <Text style={styles.pinTitle}>Pinpoint Lokasi Alamatmu</Text>
            <APIProvider apiKey={Constants.expoConfig?.extra?.GOOGLE_MAPS_API_KEY}>
              <LocationAutoComplete
                placeholder="Cari alamatmu"
                onSelectLocation={onPinChange}
              />
              <View style={styles.pinMap}>
                <MapComponent
                  style={{ width: '100%', height: 180, borderRadius: 8, overflow: 'hidden' }}
                  selectedLocation={addressCoordinate}
                  onSelectLocation={onPinChange}
                />
              </View>
              {currentAddress ? (
                <Text style={styles.pinAddress}>{currentAddress}</Text>
              ) : null}
              <TouchableOpacity
                style={[styles.pinGpsBtn, locating && { opacity: 0.7 }]}
                onPress={getCurrentLocation}
                disabled={locating}
                activeOpacity={0.85}
              >
                {locating ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <Ionicons name="location-outline" size={16} color="#FFFFFF" />
                )}
                <Text style={styles.pinGpsText}>
                  {locating ? 'Mengambil lokasi…' : 'Gunakan Lokasi Saat Ini'}
                </Text>
              </TouchableOpacity>
            </APIProvider>
            <Label text="Label alamat *" />
            <TextInput
              style={styles.input}
              value={namaAlamat}
              onChangeText={setNamaAlamat}
              placeholder="Contoh: Rumah"
              placeholderTextColor="#94A3B8"
            />
            <Label text="Nama penerima *" />
            <TextInput
              style={styles.input}
              value={namaPenerima}
              onChangeText={setNamaPenerima}
              placeholder="Nama lengkap"
              placeholderTextColor="#94A3B8"
            />
            <Label text="No. telepon *" />
            <TextInput
              style={styles.input}
              value={noTelp}
              onChangeText={setNoTelp}
              placeholder="08…"
              placeholderTextColor="#94A3B8"
              keyboardType="phone-pad"
            />
            <Label text="Alamat *" />
            <TouchableOpacity
              style={styles.areaBtn}
              onPress={() => setWilayahPickerOpen(true)}
              activeOpacity={0.85}
            >
              <Text style={[styles.areaBtnText, !kecamatan && styles.areaBtnMuted]}>
                {formatWilayahLabel(provinsi, kota, kecamatan, kodepos)
                  || 'Pilih provinsi, kota/kab, dan kode pos'}
              </Text>
            </TouchableOpacity>
            <Label text="Detail alamat *" />
            <TextInput
              style={[styles.input, styles.inputMultiline]}
              value={detailAlamat}
              onChangeText={setDetailAlamat}
              placeholder="Jalan, RT/RW, gedung"
              placeholderTextColor="#94A3B8"
              multiline
            />
            <Label text="Catatan (opsional)" />
            <TextInput
              style={styles.input}
              value={catatan}
              onChangeText={setCatatan}
              placeholder=""
              placeholderTextColor="#94A3B8"
            />
              </>
            )}
          </ScrollView>

          {formError ? <Text style={styles.formError}>{formError}</Text> : null}

          <View style={styles.actions}>
            {!blockDismiss ? (
              <TouchableOpacity
                style={styles.cancelBtn}
                onPress={onDismiss}
                disabled={saving || bootLoading}
              >
                <Text style={styles.cancelBtnText}>{isEditFlow ? 'Kembali' : 'Batal'}</Text>
              </TouchableOpacity>
            ) : (
              <View style={styles.cancelBtnPlaceholder} />
            )}
            <TouchableOpacity
              style={[styles.saveBtn, (saving || bootLoading) && styles.saveBtnDisabled]}
              onPress={() => { void onSubmit(); }}
              disabled={false}
              activeOpacity={0.85}
            >
              {saving ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text style={styles.saveBtnText}>
                  {saveLabel
                    || (isEditFlow ? 'Simpan & ke konfirmasi klaim' : 'Simpan & lanjut klaim')}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </View>

        <AddressWilayahPicker
          visible={wilayahPickerOpen}
          onClose={() => setWilayahPickerOpen(false)}
          onSelect={(area) => {
            wilayahLockedRef.current = true;
            setProvinsi(area.nama_provinsi || '');
            setKota(area.nama_kota || '');
            setKecamatan(area.nama_kecamatan || '');
            setKodepos(String(area.kodepos || ''));
            setKodeBiteship(area.kode || '');
          }}
        />
      </KeyboardAvoidingView>
    </Modal>
  );
}

function Label({ text }) {
  return <Text style={styles.label}>{text}</Text>;
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 18,
    backgroundColor: 'rgba(2, 6, 23, 0.55)',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 0,
  },
  card: {
    width: '100%',
    maxWidth: 360,
    maxHeight: '88%',
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    zIndex: 2,
    elevation: 8,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: '#0f172a',
    marginBottom: 6,
  },
  sub: {
    fontSize: 13,
    color: '#64748b',
    marginBottom: 12,
    lineHeight: 18,
  },
  scroll: { maxHeight: 460 },
  bootWrap: { paddingVertical: 28, alignItems: 'center' },
  bootText: { marginTop: 12, fontSize: 13, color: '#64748b' },
  pinTitle: {
    fontWeight: '600',
    fontSize: 13,
    color: 'rgb(71, 85, 105)',
    marginBottom: 8,
  },
  pinMap: {
    width: '100%',
    height: 180,
    marginBottom: 12,
    borderRadius: 8,
    overflow: 'hidden',
  },
  pinAddress: {
    borderWidth: 1,
    borderColor: '#1E293B',
    borderRadius: 6,
    backgroundColor: '#FFFFFF',
    fontSize: 14,
    padding: 8,
    marginBottom: 12,
    color: '#0f172a',
  },
  pinGpsBtn: {
    padding: 8,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    borderRadius: 6,
    backgroundColor: '#1E293B',
    marginBottom: 8,
  },
  pinGpsText: {
    fontWeight: '500',
    fontSize: 14,
    lineHeight: 16,
    color: '#FFFFFF',
  },
  label: {
    fontSize: 12,
    fontWeight: '600',
    color: '#334155',
    marginTop: 10,
    marginBottom: 4,
  },
  input: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 15,
    color: '#0f172a',
  },
  inputMultiline: { minHeight: 72, textAlignVertical: 'top' },
  areaBtn: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    padding: 12,
    backgroundColor: '#F8FAFC',
  },
  areaBtnText: { fontSize: 14, color: '#0f172a' },
  areaBtnMuted: { color: '#94A3B8' },
  actions: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 14,
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  cancelBtn: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#fff',
  },
  cancelBtnText: { fontSize: 14, fontWeight: '600', color: '#64748b' },
  cancelBtnPlaceholder: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    opacity: 0,
  },
  saveBtn: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#0f172a',
    alignItems: 'center',
  },
  saveBtnDisabled: { opacity: 0.7 },
  saveBtnText: { fontSize: 14, fontWeight: '600', color: '#fff' },
  formError: {
    marginTop: 10,
    fontSize: 12,
    color: '#E11D48',
    lineHeight: 16,
  },
});
